import { FunctionComponent, ReactNode, useId, useEffect, useState, useCallback, useRef, ComponentProps } from "react"
import styled from "styled-components"
import { styles } from "../../styles/themes/style";
import { Dropdown, DropdownButton } from 'react-bootstrap'
import { css } from 'styled-components'
import SvgFragment from "../../fragments/SvgFragment";
import ProfilePhoto from "../ProfilePhoto";
import TooltipWrapper from "../TooltipWrapper";
import { ChevronDown } from "lucide-react";
import { closeOtherOpenDropdowns, registerOpenDropdown, unregisterOpenDropdown } from "./openDropdownRegistry";

interface IStyledDropdownProps {
    $hideToggle?: boolean,
    selectStyle?: boolean,
    $unsetOverflow?: boolean,
    $visibleOverflow?: boolean,
    $transparent?: boolean,
    $backgroundColor?: string,
    $color?: string,
    $fontSize?: string,
    $disabled?: boolean,
    svgDisableColor?: boolean // used to disable the color of the svg when the dropdown is disabled, this is hacky but it works
    svgPath?: boolean
    $height?: string
    $padding?: string
    $overflowX?: string // used to set the overflow-x property of the dropdown menu
    panelWidth?: string
}

interface IDropdownProps extends IStyledDropdownProps {
    title?: ReactNode,
    tooltipMessage?: string,
    SVGtype?: string,
    profileImage?: boolean,
    children?: ReactNode,
    imgSource?: string,
    capitalLetters?: string,
    autoClose?: string
    drop?: string
    align?: any
    flip?: boolean
    disabled?: boolean,
    className?: string,
    show?: boolean,
    hideArrow?: boolean,
    backgroundColor?: string,
    color?: string,
    fontSize?: string,
    forceShow?: boolean // used as isOpen is not set for dropdowns which are opened automatically,
    forceClose?: boolean
    onToggle?: (isOpen: boolean) => void;
    svgPath?: boolean
    svgHoverChangeColor?: boolean
    border?: string
    panelWidth?: string
    svgDisableColor?: boolean
    popperConfig?: Record<string, unknown>
}

const DropdownMenu: FunctionComponent<IDropdownProps> = ({
    show, 
    children, 
    title, 
    tooltipMessage, 
    SVGtype, 
    imgSource, 
    disabled, 
    $hideToggle, 
    profileImage = false, 
    hideArrow = false, 
    backgroundColor,
    color,
    fontSize,
    className,
    forceShow,
    forceClose,
    onToggle,
    svgPath,
    svgHoverChangeColor = true,
    capitalLetters,
    svgDisableColor,
    $overflowX,
    selectStyle,
    popperConfig,
    ...props 
}) => {
    const dropdownId = useId();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = show !== undefined;
    const isOpen = isControlled ? show : internalIsOpen;

    const closeSelf = useCallback(() => {
        if (!isControlled) setInternalIsOpen(false);
        onToggle?.(false);
    }, [isControlled, onToggle]);

    // stop tracking a dropdown that was closed from the outside (or unmounted)
    useEffect(() => {
        if (!isOpen) unregisterOpenDropdown(dropdownId);
    }, [isOpen, dropdownId]);

    useEffect(() => () => unregisterOpenDropdown(dropdownId), [dropdownId]);

    useEffect(() => {
        if (!isControlled && !forceClose) {
            setInternalIsOpen(forceShow ?? false);
        }
    }, [forceShow, isControlled, forceClose]);

    useEffect(() => {
        if (forceClose && !isControlled) {
            setInternalIsOpen(false);
        }
    }, [forceClose, isControlled]);

    const handleToggle = (nextIsOpen: boolean, meta?: { originalEvent?: any }) => {
        if (disabled && nextIsOpen) return

        if (nextIsOpen) {
            // only one dropdown stays open at a time
            const openedNode = (meta?.originalEvent?.target as HTMLElement | undefined) ?? null;
            closeOtherOpenDropdowns(dropdownId, openedNode);
            registerOpenDropdown(dropdownId, {
                element: (openedNode?.closest?.('.dropdown, .dropup') as HTMLElement | null) ?? null,
                close: closeSelf
            });
        } else {
            unregisterOpenDropdown(dropdownId);
        }

        if (!isControlled) {
            setInternalIsOpen(nextIsOpen);
        }

        // Notify the parent component
        if (onToggle) {
            onToggle(nextIsOpen);
        }
    };

    const dropdownButtonTitleElement = (
        <TooltipWrapper tooltipText={tooltipMessage ?? ''}>
            <div className={`dropdownWrapperReference ${className || ''}`}>
                {SVGtype && <SvgFragment type={SVGtype} />}
                {title}
                {profileImage && <ProfilePhoto source={imgSource} capitalLetters={capitalLetters} />}
                {!$hideToggle && !hideArrow && (
                    selectStyle ? (
                        <ChevronIcon className="dropdown-chevron" dropdownOpen={isOpen} size={14} strokeWidth={2} />
                    ) : (
                    <StyledSvg 
                        dropdownOpen={isOpen} 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="12" 
                        height="12" 
                        viewBox="0 0 12 7"
                    >
                        <path fillRule="evenodd" d="M6.193 14.215l.623.6c.257.246.674.247.932 0L12 10.747l4.252 4.068c.258.247.675.247.932 0l.623-.6c.257-.247.257-.648 0-.896l-5.34-5.133c-.258-.248-.676-.248-.933 0l-5.34 5.133c-.258.247-.258.649 0 .896z" transform="translate(-209 -182) translate(30 120) translate(11 54) translate(162)"/>
                    </StyledSvg>
                    )
                )}
            </div>
        </TooltipWrapper>
    );

    const popperInstanceRef = useRef<{ update: () => Promise<unknown> | void } | null>(null)
    const [menuPlaced, setMenuPlaced] = useState(false)
    const positionFixed = popperConfig != null

    // Closing resets placement so the next open waits for a fresh measure.
    if (positionFixed && !isOpen && menuPlaced) setMenuPlaced(false)

    useEffect(() => {
        if (!positionFixed || !isOpen) return
        let cancelled = false
        const reveal = () => { if (!cancelled) setMenuPlaced(true) }
        const reposition = () => {
            const instance = popperInstanceRef.current
            if (!instance) {
                reveal()
                return
            }
            try {
                const pending = instance.update()
                if (pending && typeof (pending as Promise<unknown>).then === 'function') {
                    ;(pending as Promise<unknown>).then(reveal, reveal)
                } else {
                    reveal()
                }
            } catch {
                reveal()
            }
        }
        // The calendar's height is not ready on the first popper pass, so the
        // menu is parked too high. Remeasure after layout, then show it.
        const frame = requestAnimationFrame(() => requestAnimationFrame(reposition))
        const timer = window.setTimeout(reposition, 80)
        const hardStop = window.setTimeout(reveal, 160)
        return () => {
            cancelled = true
            cancelAnimationFrame(frame)
            clearTimeout(timer)
            clearTimeout(hardStop)
        }
    }, [isOpen, positionFixed])

    const fixedPopperConfig = popperConfig ? {
        ...popperConfig,
        modifiers: [
            ...(Array.isArray(popperConfig.modifiers) ? popperConfig.modifiers : []),
            {
                name: 'captureInstance',
                enabled: true,
                phase: 'afterWrite',
                fn: ({ instance }: { instance?: { update: () => Promise<unknown> | void } }) => {
                    if (instance) popperInstanceRef.current = instance
                },
            },
        ],
    } : undefined

    const surfaceProps = {
        $disabled: disabled,
        $hideToggle,
        $backgroundColor: backgroundColor,
        $color: color,
        $fontSize: fontSize,
        $height: props.$height,
        $padding: props.$padding,
        className,
        svgPath,
        svgDisableColor,
        selectStyle,
        $overflowX,
    }

    // Wide panels (the tasks due-date calendar) pass popperConfig so the menu
    // can anchor to the trigger's right edge and escape the sidebar overflow.
    if (popperConfig) {
        const { align, flip, autoClose, drop } = props
        return (
            <StyledDropdown
                {...surfaceProps}
                data-placed={menuPlaced ? 'true' : 'false'}
                align={align}
                autoClose={autoClose}
                drop={drop}
                show={isOpen}
                onToggle={handleToggle}
            >
                <Dropdown.Toggle id={dropdownId} childBsPrefix="actions-dropdown" disabled={disabled}>
                    {dropdownButtonTitleElement}
                </Dropdown.Toggle>
                <Dropdown.Menu popperConfig={fixedPopperConfig as ComponentProps<typeof Dropdown.Menu>['popperConfig']} flip={flip ?? false}>
                    {children}
                </Dropdown.Menu>
            </StyledDropdown>
        )
    }

    return (
        <StyledDropownButton 
            {...surfaceProps}
            disabled={disabled}
            show={isOpen} 
            bsPrefix="actions-dropdown" 
            title={dropdownButtonTitleElement} 
            onToggle={handleToggle} 
            svgHoverChangeColor={svgHoverChangeColor}
            {...props}
        >
            {children}
        </StyledDropownButton>
    );
}

const dropdownSurfaceStyles = css<IStyledDropdownProps>` 
    background-color: ${({ theme: { secondaryColor }, $backgroundColor }) => $backgroundColor ?? secondaryColor};
    ${({$transparent}) => $transparent && `
        background-color: transparent;`}
    border-radius: 3px;
    transition: all .2s ease;
    .dropdown-toggle, .dropdownWrapperReference {
        display: flex;
        align-items: center;
        gap: 5px;  
        font-family: NunitoSans;
        font-size: ${({ $fontSize }) => $fontSize ?? '14px'};
        font-weight: bold;
        line-height: 20px;
        padding: ${({ $padding }) => $padding ?? '0px'};
        min-height: ${({ $height }) => $height ?? 'auto'};
        border-radius: 4px;
        border: none;
        width: 100%;
        background: transparent;
        color: ${({ $color }) => $color ?? styles.colors.primary600};
        display: flex;
        align-items: center;
        &:hover {
            .LDtooltip { display: block; }
            color: ${styles.colors.primary500};
            border: none;
            /* lucide icons are stroke based, filling their paths turns them into solid blobs */
            svg:not(.lucide) path { ${({ svgPath, svgDisableColor }) => `${svgPath ? 'stroke' : 'fill'}: ${svgDisableColor ? styles.colors.black300 : styles.colors.primary600};`}}
            .dropdown-chevron { color: ${styles.colors.black500}; opacity: 1; }
        }
    }
    .actions-dropdown + div{
        padding: 5px 0px;
        max-height: 500px;
        overflow-y: auto;
        ${({$unsetOverflow}) => $unsetOverflow && `overflow: unset;`}
        ${({$visibleOverflow}) => $visibleOverflow && `overflow: visible;`}
        width: ${({ panelWidth }) => panelWidth ?? 'auto'};
        min-width: ${({ panelWidth }) => panelWidth ?? '170px'};
        z-index: 1005;
        box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.1);
        border: none;
        background-color: ${({ theme: { secondaryColor } }) => secondaryColor};
        ${({$overflowX}) => $overflowX && `overflow-x: ${$overflowX};`}
    }
    ${( { selectStyle, $disabled, theme: { quinaryColor } } ) => selectStyle && `
        button, .dropdown-item {
            font-weight: normal;
        }
        .dropdown-toggle {
            width: 100%;
            outline: 2px solid ${quinaryColor};
            padding: 0px 6px;
            &:hover, &:focus { outline: 2px solid ${($disabled ? quinaryColor : styles.colors.primary600)}; }
        }`}
    .dropdown-toggle::after {
        display: none !important; 
    }
    ${({$disabled, theme: { senaryColor, quinaryColor }, svgDisableColor }) => $disabled && `
        background-color: ${senaryColor};
        border: 1px solid transparent;
        cursor: default;

        ${!svgDisableColor && `
            & svg:not(.lucide) { path { fill: ${quinaryColor} !important; } }
            & svg.lucide { color: ${quinaryColor} !important; }
        `}
    `}
`

const StyledDropownButton = styled(DropdownButton)<IStyledDropdownProps>`
    ${dropdownSurfaceStyles}
`
const StyledDropdown = styled(Dropdown)<IStyledDropdownProps>`
    ${dropdownSurfaceStyles}
    &[data-placed='false'] .actions-dropdown + div,
    &[data-placed='false'] .dropdown-menu {
        visibility: hidden !important;
    }
`

const ChevronIcon = styled(ChevronDown)<{ dropdownOpen: boolean }>`
    flex-shrink: 0;
    margin-left: auto;
    color: ${styles.colors.black300};
    transition: transform 0.15s ease, color 0.15s ease;
    transform: ${({ dropdownOpen }) => (dropdownOpen ? 'rotate(180deg)' : 'none')};
`

const StyledSvg = styled.svg<{ dropdownOpen: boolean }>`
    fill: ${({dropdownOpen, theme: { quaternaryColor }}) => dropdownOpen ? quaternaryColor: styles.colors.black300 };
    transition: all .2s ease;
    cursor: pointer;
    transform: rotate(180deg);
    ${({dropdownOpen}) => dropdownOpen && 'transform: rotate(360deg);'}
    &:hover{ fill: ${props => props.theme.primaryColor }};
`
export default DropdownMenu